import ky from 'ky';
import { type IUser } from 'src/api/users-api';
import { createBearerHeader } from 'src/common/api-utils';
import { Config } from 'src/config';
import {type IStandardErrorResponse, type IStandardSuccessResponse} from 'src/api/common';
import type { LoginResponse } from 'src/services/api';
import { simpleKy } from 'src/services/api/ky/simple-ky';

console.debug(`Using API server ${Config.backendServer}`);

interface IAdminLoginResponse {
    access_token: string;
    msg: string;
    status: string;
}

/**
 * Split out the admin API into its own file so it is not seen by consumer pages
 * Uses ky
 * @throws {HTTPError} on failure
 */
export async function login ({ email, password }: {email: string, password: string}): Promise<IAdminLoginResponse> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/auth/login';
    const data = {
        email: email,
        password: password
    };
    return ky.post(url.toString(), {
        json: data,
    }).json() as Promise<IAdminLoginResponse>;
}

export interface IAdminUser {
    email: string;
}

// NOTE: this is for debugging
export const debugRequestError = async (r: Response) => {
    if (r.headers.get('Content-Type') === 'application/json') {
        const j = await r.json();
        console.error('request failed');
        console.error(`status code was: ${r.status}`);
        console.error('message from the server was: ');
        console.error(j);
    }
};

/**
 * Uses ky
 * @throws {HTTPError} on failure
 */
export async function getCurrentAdminUser (accessToken: string): Promise<IAdminUser> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/user/me';
    const headers = createBearerHeader(accessToken);
    return ky.get(url.toString(), {
        headers : headers,
    }).json() as Promise<IAdminUser>;
}

export interface IBetaRequester {
    email: string;
    inserted_at: string;
}

/**
 * Uses ky
 * @throws {HTTPError} on failure
 */
export async function getBetaRequesters (accessToken: string): Promise<IBetaRequester[]> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/beta-requesters';
    const headers = createBearerHeader(accessToken);
    return ky.get(url.toString(), {
        headers: headers,
    }).json() as Promise<IBetaRequester[]>;
}

interface ILandlordUsersResponse {
    users: IUser[];
}

/**
 * Uses ky
 * @throws {HTTPError} on failure
 */
export async function getLandlordUsers (accessToken: string): Promise<ILandlordUsersResponse> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/users/all-landlords';
    const headers = createBearerHeader(accessToken);
    return ky.get(url.toString(), {
        headers: headers,
    }).json() as Promise<ILandlordUsersResponse>;
}

export interface ILandlordOnboardInvite {
    /**
     * The invite code sent to the landlord
     */
    code: string;
    to_email: string;
    to_name: string | null;
    to_company: string | null;
    is_company_admin: boolean;
    created_at: Date;
    expires_at: Date;
}

/**
 * Uses ky
 * @throws {HTTPError} on failure
 */
export async function getLandlordOnboardInvites (accessToken: string): Promise<ILandlordOnboardInvite[]> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/landlord-onboard-invites';
    const headers = createBearerHeader(accessToken);
    const invites = await ky.get(url.toString(), {
        headers: headers,
    }).json() as ILandlordOnboardInvite[];
    invites.forEach((invite) => {
        invite.created_at = new Date(invite.created_at);
        invite.expires_at = new Date(invite.expires_at);
    });
    return invites;
}

export type INewLandlordOnboardInvite = {
    to_email: string;
    to_name: string;
    to_company: string;
    is_company_admin: boolean;
    /**
     * Whether to bypass the billing page
     * Means the client will be invoiced separately
     */
    is_bypass_billing_during_onboard: boolean;
}

export interface IMessageResponse {
    status: string;
    msg: string;
    error_code?: string;
}

export interface IDemoUser {
    email: string;
    password: string;
    name: string;
    company: string;
}

/**
 * Uses ky
 * @throws {HTTPError} on failure
 */
export async function createLandlordOnboardInvite (accessToken: string, invite: INewLandlordOnboardInvite) : Promise<IMessageResponse> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/landlord-onboard-invites';
    const data = invite;
    const headers = createBearerHeader(accessToken);
    return await ky.post(url.toString(), {
        headers: headers,
        json: data,
    }).json() as IMessageResponse;
}

export async function deleteInvite(accessToken: string, code: string): Promise<void>{
    const url = new URL(Config.backendServer);
    url.pathname = `/api/admin/landlord-onboard-invites/${code}`;
    const headers = createBearerHeader(accessToken);

    await ky.delete(url.toString(), {
        headers,
    });
}
export async function deleteAllOutdatedInvites(accessToken: string): Promise<void>{
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/landlord-onboard-invites/all';
    const headers = createBearerHeader(accessToken);

    await ky.delete(url.toString(), {
        headers,
    });
}

export async function cleanUpDemoUserData(accessToken: string, id: number): Promise<IStandardSuccessResponse<null> | IStandardErrorResponse> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/users/demo_accounts/cleanup';
    const headers = createBearerHeader(accessToken);

    const res = await ky.post(url.toString(), {
        headers,
        json: {
            id,
        },
    });
    return res.json();
}

export async function createDemoUser(accessToken: string, user: IDemoUser): Promise<IStandardSuccessResponse<null>> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/users/demo_accounts';
    const headers = createBearerHeader(accessToken);

    const res = await ky.post(url.toString(), {
        headers,
        json: user,
    });
    return res.json();
}

export async function deleteDemoUser(accessToken: string, id: number): Promise<IStandardSuccessResponse<null>> {
    const url = new URL(Config.backendServer);
    url.pathname = `/api/admin/users/demo_accounts/${id}`;
    const headers = createBearerHeader(accessToken);

    const res = await ky.delete(url.toString(), {
        headers
    });

    return res.json();
}

export async function loginAsUser(accessToken: string, email: string): Promise<Pick<IAdminLoginResponse, 'access_token'> & { 'refresh_token': string }> {
    const url = new URL(Config.backendServer);
    url.pathname = '/api/admin/users/login_as/' + email;
    const headers = createBearerHeader(accessToken);

    const res = await ky.get(url.toString(), {
        headers
    });
    return res.json();
}

export async function getLandlordAuthTokens(token: string) {
    return simpleKy('api/auth/landlord/one-time-token-login', {
        method: 'post',
        json: { token },
    }).json<Omit<LoginResponse, 'user'>>();
}

export default {
    login,
    getCurrentAdminUser,
    getBetaRequesters,
    getLandlordUsers,
    getLandlordOnboardInvites,
    createLandlordOnboardInvite,
    deleteInvite,
    deleteAllOutdatedInvites,
    cleanUpDemoUserData,
    createDemoUser,
    deleteDemoUser,
    loginAsUser,
    getLandlordAuthTokens
};
